import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Stack,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  CONSTRUCTION_CATEGORIES,
  ConstructionCategory,
  ConstructionFieldSpec,
  ConstructionModifier,
  ConstructionSetting,
  changedFields,
  resolveConstructionPreview,
  useCreateConstructionSetting,
  useUpdateConstructionSetting,
} from "../../api/constructionTypes";
import { C, formatNum, inputSx, primaryBtnSx } from "./constructionUi";

interface Props {
  open: boolean;
  setting: ConstructionSetting | null;
  fields: ConstructionFieldSpec[];
  onClose: (saved: boolean) => void;
}

const OP_LABELS: Record<string, string> = {
  set_baseline: "Set to",
  scale: "Multiply by",
  add: "Add",
};

export function ConstructionSettingEditor({ open, setting, fields, onClose }: Props) {
  const isEdit = !!setting;
  const create = useCreateConstructionSetting();
  const update = useUpdateConstructionSetting();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<ConstructionCategory>("other");
  const [modifiers, setModifiers] = useState<ConstructionModifier[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (setting) {
      setName(setting.name);
      setDescription(setting.description);
      setCategory(setting.category);
      setModifiers([...setting.modifiers]);
    } else {
      setName("");
      setDescription("");
      setCategory("other");
      setModifiers([]);
    }
    setError(null);
  }, [setting, open]);

  const fieldByName = useMemo(() => {
    const m: Record<string, ConstructionFieldSpec> = {};
    for (const f of fields) m[f.name] = f;
    return m;
  }, [fields]);

  const defaultOpFor = (fieldName: string): ConstructionModifier["op"] =>
    (fieldByName[fieldName]?.ops[0] as ConstructionModifier["op"]) ?? "scale";

  const addModifier = () => {
    const field = fields[0]?.name ?? "q_cr";
    const spec = fieldByName[field];
    setModifiers([...modifiers, { field, op: defaultOpFor(field), value: spec?.default ?? 1 }]);
  };

  const updateModifier = (idx: number, patch: Partial<ConstructionModifier>) => {
    setModifiers(
      modifiers.map((m, i) => {
        if (i !== idx) return m;
        const next = { ...m, ...patch };
        if (patch.field && patch.field !== m.field) {
          const spec = fieldByName[next.field];
          if (spec) {
            if (!spec.ops.includes(next.op as any)) next.op = spec.ops[0] as ConstructionModifier["op"];
            next.value = spec.default;
          }
        }
        return next;
      })
    );
  };

  const removeModifier = (idx: number) =>
    setModifiers(modifiers.filter((_, i) => i !== idx));

  const preview = useMemo(() => {
    const resolved = resolveConstructionPreview(modifiers, fields);
    return changedFields(resolved, fields).map((f) => ({
      label: f.label,
      units: f.units,
      value: resolved[f.name],
    }));
  }, [modifiers, fields]);

  const handleSubmit = async () => {
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Give your characteristic a name.");
      return;
    }
    const baselineFields = modifiers.filter((m) => m.op === "set_baseline").map((m) => m.field);
    const dupes = baselineFields.filter((f, i) => baselineFields.indexOf(f) !== i);
    if (dupes.length) {
      const label = fieldByName[dupes[0]]?.label ?? dupes[0];
      setError(`“${label}” has more than one “Set to” rule — only one is allowed per field.`);
      return;
    }
    try {
      const body = { name: trimmed, description, category, modifiers };
      if (isEdit) await update.mutateAsync({ id: setting!.id, body });
      else await create.mutateAsync(body);
      onClose(true);
    } catch (e: any) {
      setError(e?.message || "Failed to save characteristic");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { backgroundColor: "#161927", color: C.text, borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>
        {isEdit ? "Edit characteristic" : "New structure characteristic"}
      </DialogTitle>
      <DialogContent sx={{ pt: "20px !important" }}>
        <Stack spacing={2.5}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Name"
              placeholder="e.g. Boxed-in eaves"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              autoFocus
              sx={inputSx}
            />
            <FormControl size="small" sx={{ ...inputSx, minWidth: 200 }}>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value as ConstructionCategory)}
              >
                {CONSTRUCTION_CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            size="small"
            sx={inputSx}
          />

          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
              <Typography sx={{ color: C.text, fontWeight: 600 }}>
                Effects on fire behaviour
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addModifier}
                sx={{ color: C.accent, textTransform: "none" }}
              >
                Add effect
              </Button>
            </Box>
            <Typography variant="caption" sx={{ color: C.faint, mb: 1.5, display: "block" }}>
              Each effect adjusts one physics parameter. “Set to” fixes a value,
              “Multiply by” scales it, “Add” offsets it.
            </Typography>

            {modifiers.length === 0 ? (
              <Box
                sx={{
                  border: `1px dashed ${C.border}`,
                  borderRadius: 2,
                  py: 2.5,
                  textAlign: "center",
                }}
              >
                <Typography variant="caption" sx={{ color: C.faint }}>
                  No effects yet — this characteristic won't change anything.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {modifiers.map((m, idx) => {
                  const spec = fieldByName[m.field];
                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0,1fr) 130px 110px 36px",
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <FormControl size="small" sx={inputSx}>
                        <InputLabel>Parameter</InputLabel>
                        <Select
                          label="Parameter"
                          value={m.field}
                          onChange={(e) => updateModifier(idx, { field: e.target.value })}
                        >
                          {fields.map((f) => (
                            <MenuItem key={f.name} value={f.name}>
                              {f.label}
                              {f.units ? ` (${f.units})` : ""}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={inputSx}>
                        <InputLabel>How</InputLabel>
                        <Select
                          label="How"
                          value={m.op}
                          onChange={(e) =>
                            updateModifier(idx, { op: e.target.value as ConstructionModifier["op"] })
                          }
                        >
                          {(spec?.ops ?? []).map((op) => (
                            <MenuItem key={op} value={op}>
                              {OP_LABELS[op] ?? op}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        type="number"
                        label="Value"
                        value={m.value}
                        inputProps={{ step: 0.1, min: spec?.min, max: spec?.max }}
                        onChange={(e) => updateModifier(idx, { value: Number(e.target.value) })}
                        sx={inputSx}
                      />
                      <IconButton size="small" onClick={() => removeModifier(idx)} sx={{ color: C.danger }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                      {spec?.help && (
                        <Typography variant="caption" sx={{ color: C.faint, gridColumn: "1 / -1", mt: -0.5 }}>
                          {spec.help}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>

          {/* Live preview */}
          {preview.length > 0 && (
            <Box sx={{ p: 1.75, borderRadius: 2, backgroundColor: C.panel, border: `1px solid ${C.border}` }}>
              <Typography
                sx={{
                  color: C.faint,
                  fontSize: "0.66rem",
                  fontWeight: 700,
                  letterSpacing: 0.7,
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Net effect on a baseline building
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {preview.map((p) => (
                  <Chip
                    key={p.label}
                    label={`${p.label}: ${formatNum(p.value)}${p.units ? " " + p.units : ""}`}
                    size="small"
                    sx={{
                      height: 22,
                      backgroundColor: C.accentSoft,
                      color: C.accent,
                      "& .MuiChip-label": { px: 1, fontSize: "0.7rem" },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${C.border}` }}>
        <Button onClick={() => onClose(false)} sx={{ color: C.dim, textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={create.isPending || update.isPending}
          sx={primaryBtnSx}
        >
          {isEdit ? "Save changes" : "Create characteristic"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
